import type { AudioProcessor } from "@twilio/voice-sdk";

/**
 * Streams an <audio> element into the call via Web Audio.
 * - createProcessedStream() returns a MediaStream the Voice SDK will treat as the "mic".
 * - play()/pause()/setVolume() control the element.
 */
export class BackgroundAudioProcessor implements AudioProcessor {
	private ctx: AudioContext | null = null;
	private element?: HTMLAudioElement;
	private source?: MediaElementAudioSourceNode;
	private dest?: MediaStreamAudioDestinationNode;

	constructor(private url: string) {}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	async createProcessedStream(_micStream: MediaStream): Promise<MediaStream> {
		if (!this.ctx) {
			this.ctx = new AudioContext();
		}

		const el = new Audio();

		// If media is cross-origin, server must send CORS headers
		el.crossOrigin = "anonymous";
		el.src = this.url;
		el.preload = "auto";
		this.element = el;

		const src = this.ctx.createMediaElementSource(el);
		const dst = this.ctx.createMediaStreamDestination();
		this.source = src;
		this.dest = dst;

		// To monitor locally, also connect to destination:
		src.connect(this.ctx.destination);

		src.connect(dst);

		// Return the stream Twilio will capture/encode
		return dst.stream;
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	async destroyProcessedStream(_processed: MediaStream): Promise<void> {
		try {
			this.source?.disconnect();
		} catch (err) {
			console.warn("Failed to disconnect source", err);
		}

		try {
			this.dest?.disconnect();
		} catch (err) {
			console.warn("Failed to disconnect dest", err);
		}

		try {
			this.element?.pause();
		} catch (err) {
			console.warn("Failed to pause element", err);
		}

		this.source = undefined;
		this.dest = undefined;
		this.element = undefined;

		// Keep AudioContext alive in case we reuse the processor
		// await this.ctx?.close(); this.ctx = null;
	}

	async play(): Promise<void> {
		if (!this.ctx) {
			return;
		}

		try {
			await this.ctx.resume();
		} catch (err) {
			console.warn("Failed to resume", err);
		}

		try {
			await this.element?.play();
		} catch (err) {
			console.warn("Failed to play", err);
		}
	}

	pause() {
		this.element?.pause();
	}

	setVolume(v: number) {
		if (this.element) {
			this.element.volume = v;
		}
	}

	getElement(): HTMLAudioElement | undefined {
		return this.element;
	}

	updateUrl(next: string) {
		if (this.element) {
			this.element.src = next;
		}
	}
}
